import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import AudioService, { AUDIO_EFFECT } from "../../services/AudioService";
import { usePagesTranslation } from "../../pages/locales";
import SlidingLineCompleteFx, {
    LINE_COMPLETE_BANNER_MS,
    LINE_COMPLETE_SNAP_MS,
    cellSnapLockKeyframes,
} from "../../pages/puzzles/sliding/SlidingLineCompleteFx";
import { slidingEmptySlotBackground } from "./slidingEmptySlotStyle";

/** True when `fromVisual` shares a row or column with the empty slot (clickable for a slide). */
function canSlideTowardEmpty(gs, fromVisual, emptyVisual) {
    if (fromVisual < 0 || emptyVisual < 0 || fromVisual === emptyVisual || gs < 1) return false;
    const fr = Math.floor(fromVisual / gs);
    const fc = fromVisual % gs;
    const er = Math.floor(emptyVisual / gs);
    const ec = emptyVisual % gs;
    return fr === er || fc === ec;
}

/**
 * Shift every tile between the click and the empty slot toward the hole in one move.
 * Empty ends at `fromVisual`. Returns a new layout, or null if the click is invalid.
 */
function applySlideTowardEmpty(layout, gs, fromVisual, emptyVisual) {
    if (!Array.isArray(layout) || !canSlideTowardEmpty(gs, fromVisual, emptyVisual)) return null;
    const next = [...layout];
    const fr = Math.floor(fromVisual / gs);
    const fc = fromVisual % gs;
    const er = Math.floor(emptyVisual / gs);
    const ec = emptyVisual % gs;
    const emptySlice = layout[emptyVisual];

    if (fr === er) {
        const row = fr;
        if (fc > ec) {
            for (let c = ec; c < fc; c += 1) {
                next[row * gs + c] = layout[row * gs + c + 1];
            }
        } else {
            for (let c = ec; c > fc; c -= 1) {
                next[row * gs + c] = layout[row * gs + c - 1];
            }
        }
        next[row * gs + fc] = emptySlice;
    } else {
        const col = fc;
        if (fr > er) {
            for (let r = er; r < fr; r += 1) {
                next[r * gs + col] = layout[(r + 1) * gs + col];
            }
        } else {
            for (let r = er; r > fr; r -= 1) {
                next[r * gs + col] = layout[(r - 1) * gs + col];
            }
        }
        next[fr * gs + col] = emptySlice;
    }
    return next;
}

/** All cells in the empty slot's row and column (any distance) — clickable for a multi-tile slide. */
function movableTargetsOf(gs, emptyVisual) {
    if (emptyVisual < 0 || gs < 1) return [];
    const row = Math.floor(emptyVisual / gs);
    const col = emptyVisual % gs;
    const targets = [];
    for (let c = 0; c < gs; c += 1) {
        if (c === col) continue;
        targets.push({ cell: row * gs + c, point: c < col ? "left" : "right" });
    }
    for (let r = 0; r < gs; r += 1) {
        if (r === row) continue;
        targets.push({ cell: r * gs + col, point: r < row ? "up" : "down" });
    }
    return targets;
}

/** Cursor tip aims into the tile the player can click. */
function clickHintRotation(point) {
    if (point === "up") return 45;
    if (point === "right") return 135;
    if (point === "down") return 225;
    return -45;
}

function normalizeLayout(startingLayout, n) {
    if (!Array.isArray(startingLayout) || startingLayout.length !== n || n <= 0) return [];
    return startingLayout.map((x) => Number(x));
}

function isIdentityLayout(layout) {
    return Array.isArray(layout) && layout.length > 0 && layout.every((v, i) => Number(v) === i);
}

function isRowComplete(layout, gs, row) {
    for (let c = 0; c < gs; c += 1) {
        const i = row * gs + c;
        if (Number(layout[i]) !== i) return false;
    }
    return true;
}

function isColComplete(layout, gs, col) {
    for (let r = 0; r < gs; r += 1) {
        const i = r * gs + col;
        if (Number(layout[i]) !== i) return false;
    }
    return true;
}

/** Newly completed rows/columns only (popup is for full line matches, not single cells). */
export function findLineMatchCelebration(prev, next, gs) {
    if (!Array.isArray(prev) || !Array.isArray(next) || prev.length !== next.length || gs < 1) {
        return null;
    }
    // Full-board solve uses SuccessCard / confetti — skip mid-game popup.
    if (isIdentityLayout(next)) return null;

    const rows = [];
    const cols = [];
    for (let r = 0; r < gs; r += 1) {
        if (isRowComplete(next, gs, r) && !isRowComplete(prev, gs, r)) {
            rows.push(r);
        }
    }
    for (let c = 0; c < gs; c += 1) {
        if (isColComplete(next, gs, c) && !isColComplete(prev, gs, c)) {
            cols.push(c);
        }
    }
    if (rows.length === 0 && cols.length === 0) return null;

    const cells = [];
    const seen = new Set();
    for (const r of rows) {
        for (let c = 0; c < gs; c += 1) {
            const i = r * gs + c;
            if (!seen.has(i)) {
                seen.add(i);
                cells.push(i);
            }
        }
    }
    for (const c of cols) {
        for (let r = 0; r < gs; r += 1) {
            const i = r * gs + c;
            if (!seen.has(i)) {
                seen.add(i);
                cells.push(i);
            }
        }
    }

    return { cells, rows, cols };
}

/** Gutters between tiles — keep in sync with `StoryReferenceWithGrid` GRID_GAP_PX (2px). */
const SLIDING_GRID_GAP_PX = 2;

/** Tile scatter duration before the parent shows the full solved image. */
const SOLVE_REVEAL_MS = 1200;

/** 1-indexed odd rows/cols fly right/up; even fly left/down. */
function getTileRevealTransform(visualCellIdx, gs) {
    const row = Math.floor(visualCellIdx / gs);
    const col = visualCellIdx % gs;
    const distance = "115%";
    const tx = row % 2 === 0 ? distance : `-${distance}`;
    const ty = col % 2 === 0 ? `-${distance}` : distance;
    return `translate(${tx}, ${ty})`;
}

/**
 * Image is divided into gridSize×gridSize equal cells in row-major order (indices 0 … n−1).
 * `layout[visualCell]` = which slice index sits in that board cell (permutation).
 * The hole is the slice with index n−1 (bottom-right in the solved picture).
 */
export default function SlidingPlayMain({
    imageUrl,
    gridSize,
    startingLayout,
    /** Tile index rendered as empty slot; default last slice (n−1) */
    emptyTileIndex = null,
    /** Max width of the board; number → `{ xs: '100%', sm: n }`. Default fills parent width. */
    maxBoardWidth = null,
    /** Called whenever the board permutation changes (for reference overlay sync). */
    onLayoutChange = null,
    /** Called after each valid tile slide (one move). */
    onMove = null,
    /** When solved, message below the board (e.g. translated). Omit for default "Completed". */
    completedLabel,
    /** When true, do not render the solved caption under the board (e.g. parent shows SuccessCard). */
    hideCompletedCaption = false,
    /** Animate tiles flying off before `onSolvedRevealComplete` (board play). */
    animateSolveReveal = false,
    /** Fired once after the solve scatter animation finishes. */
    onSolvedRevealComplete = null,
    /** When true, board fills a square parent (`width`/`height` 100%) instead of sizing from width alone. */
    fillParent = false,
    /** When false, tile-move sound effects are skipped. */
    playMoveSound = true,
    /**
     * Ghost guide overlay (hint). Does not change the real layout.
     * `{ mode: 'step'|'all', moves: [{ from, to, sliceIndex }], stepIndex?: number }`
     */
    ghostGuide = null,
    /** When false, step number badges are omitted (screenshot can draw its own). */
    ghostGuideShowLabels = true,
    /** Color visible in grid gaps between tiles (e.g. `#fff` on dark mobile play). */
    gridGutterColor = null,
    /** Outer board border color; defaults to theme `divider`. */
    gridBorderColor = null,
    /** Static preview — no tile clicks, hover, or keyboard moves (level cards, browse thumbnails). */
    readOnly = false,
    /** When > 0, tiles slide smoothly between cells (animation capture). */
    slideTransitionMs = 0,
    /** Mid-game praise when tiles / rows / columns land correctly. */
    showMatchCelebrations = true,
    /** Solid gray hole with no photo showing through (landing solved pose). */
    solidEmptySlot = false,
    /** On the play page, pulse a click cursor on each tile that can slide into the hole. */
    showStartClickHints = false,
    /** Replace each tile picture with its solved position (1 = top-left, row by row). */
    showTileNumbers = false,
    /** Number-tile fill: `light` (cream) or `dark` (forest). */
    tileNumberTheme = "light",
}) {
    const { t } = usePagesTranslation();
    const gs = Number(gridSize);
    const n = Number.isFinite(gs) && gs > 0 ? gs * gs : 0;
    const emptyIdx =
        emptyTileIndex != null && Number.isFinite(Number(emptyTileIndex))
            ? Number(emptyTileIndex)
            : n > 0
              ? n - 1
              : -1;

    const [layout, setLayout] = useState(() => normalizeLayout(startingLayout, n));
    const [revealActive, setRevealActive] = useState(false);
    const [revealImageVisible, setRevealImageVisible] = useState(false);
    const [matchFx, setMatchFx] = useState(null);
    const [startClickHintsOn, setStartClickHintsOn] = useState(showStartClickHints);
    const revealCompleteFiredRef = useRef(false);
    const matchFxClearRef = useRef(null);
    const matchFxIdRef = useRef(0);

    const onLayoutChangeRef = useRef(onLayoutChange);
    const onMoveRef = useRef(onMove);
    const onSolvedRevealCompleteRef = useRef(onSolvedRevealComplete);
    useEffect(() => {
        onLayoutChangeRef.current = onLayoutChange;
        onMoveRef.current = onMove;
        onSolvedRevealCompleteRef.current = onSolvedRevealComplete;
    });

    useEffect(() => {
        onLayoutChangeRef.current?.(layout);
    }, [layout]);

    const layoutRef = useRef(layout);
    layoutRef.current = layout;

    useEffect(() => {
        AudioService.preload(AUDIO_EFFECT.SLIDING_MOVE);
        AudioService.preload(AUDIO_EFFECT.SLIDING_CELL_MATCH);
        AudioService.preload(AUDIO_EFFECT.SLIDING_LINE_MATCH);
        AudioService.preload(AUDIO_EFFECT.SLIDING_BONUS);
        return () => {
            AudioService.release(AUDIO_EFFECT.SLIDING_MOVE);
            AudioService.release(AUDIO_EFFECT.SLIDING_CELL_MATCH);
            AudioService.release(AUDIO_EFFECT.SLIDING_LINE_MATCH);
            AudioService.release(AUDIO_EFFECT.SLIDING_BONUS);
            if (matchFxClearRef.current) {
                window.clearTimeout(matchFxClearRef.current);
            }
        };
    }, []);

    const pickPraise = useCallback(() => {
        const keys = [
            "puzzle.sliding.praise-row-complete",
            "puzzle.sliding.praise-good-job",
            "puzzle.sliding.praise-great",
            "puzzle.sliding.praise-excellent",
        ];
        const fallbacks = ["Row Complete!", "Good Job!", "Great!", "Excellent!"];
        const i = Math.floor(Math.random() * keys.length);
        const translated = t(keys[i]);
        return translated === keys[i] ? fallbacks[i] : translated;
    }, [t]);

    const clearMatchFx = useCallback((id) => {
        setMatchFx((cur) => (cur?.id === id ? null : cur));
    }, []);

    const triggerMatchCelebration = useCallback(
        (prev, next) => {
            if (!showMatchCelebrations) return;
            const found = findLineMatchCelebration(prev, next, gs);
            if (!found) return;

            if (matchFxClearRef.current) {
                window.clearTimeout(matchFxClearRef.current);
            }
            matchFxIdRef.current += 1;
            const id = matchFxIdRef.current;
            setMatchFx({
                id,
                cells: found.cells,
                rows: found.rows,
                cols: found.cols,
                praise: pickPraise(),
            });
            if (playMoveSound) {
                void AudioService.play(AUDIO_EFFECT.SLIDING_LINE_MATCH);
                window.setTimeout(() => {
                    void AudioService.play(AUDIO_EFFECT.SLIDING_BONUS);
                }, 180);
            }
            // Safety unmount if child onComplete is missed.
            matchFxClearRef.current = window.setTimeout(() => {
                clearMatchFx(id);
            }, LINE_COMPLETE_BANNER_MS + 50);
        },
        [clearMatchFx, gs, pickPraise, playMoveSound, showMatchCelebrations],
    );

    useEffect(() => {
        const next = normalizeLayout(startingLayout, n);
        const prev = layoutRef.current;
        const same =
            prev.length === next.length && prev.every((v, i) => Number(v) === Number(next[i]));
        if (same) return;
        if (showMatchCelebrations) {
            triggerMatchCelebration(prev, next);
        } else {
            setMatchFx(null);
        }
        setLayout(next);
    }, [startingLayout, n, showMatchCelebrations, triggerMatchCelebration]);

    const handleTileActivate = useCallback(
        (visualCellIdx) => {
            const prev = layoutRef.current;
            if (prev.length !== n) return;
            const emptyVisual = prev.findIndex((v) => Number(v) === emptyIdx);
            if (emptyVisual < 0) return;
            const next = applySlideTowardEmpty(prev, gs, visualCellIdx, emptyVisual);
            if (!next) return;
            setStartClickHintsOn(false);
            setLayout(next);
            const lineMatched = Boolean(findLineMatchCelebration(prev, next, gs));
            if (playMoveSound && !lineMatched) {
                AudioService.play(AUDIO_EFFECT.SLIDING_MOVE);
            }
            onMoveRef.current?.();
            triggerMatchCelebration(prev, next);
        },
        [emptyIdx, gs, n, playMoveSound, triggerMatchCelebration],
    );

    const isSolved =
        n > 0 && layout.length === n && layout.every((v, i) => Number(v) === i);

    useEffect(() => {
        if (!isSolved) {
            revealCompleteFiredRef.current = false;
            setRevealActive(false);
            setRevealImageVisible(false);
        } else {
            setMatchFx(null);
        }
    }, [isSolved]);

    useEffect(() => {
        if (!isSolved || !animateSolveReveal) return;
        let cancelled = false;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!cancelled) setRevealActive(true);
            });
        });
        return () => {
            cancelled = true;
        };
    }, [isSolved, animateSolveReveal]);

    useEffect(() => {
        if (!revealActive) return;
        const imageTimer = window.setTimeout(
            () => setRevealImageVisible(true),
            Math.round(SOLVE_REVEAL_MS * 0.35)
        );
        const doneTimer = window.setTimeout(() => {
            if (revealCompleteFiredRef.current) return;
            revealCompleteFiredRef.current = true;
            onSolvedRevealCompleteRef.current?.();
        }, SOLVE_REVEAL_MS);
        return () => {
            window.clearTimeout(imageTimer);
            window.clearTimeout(doneTimer);
        };
    }, [revealActive]);

    if (!imageUrl || typeof imageUrl !== "string" || !imageUrl.trim()) {
        return (
            <Typography variant="caption" color="text.secondary">
                No image URL
            </Typography>
        );
    }

    if (!Number.isFinite(gs) || gs < 1 || !Array.isArray(startingLayout) || startingLayout.length !== n) {
        return (
            <Typography variant="caption" color="text.secondary">
                Invalid grid or layout (expect length {n || "gridSize²"})
            </Typography>
        );
    }

    if (layout.length !== n) {
        return (
            <Typography variant="caption" color="text.secondary">
                Invalid grid or layout (expect length {n || "gridSize²"})
            </Typography>
        );
    }

    const emptyVisualPos = layout.findIndex((v) => Number(v) === emptyIdx);
    const useSmoothSlides = Number(slideTransitionMs) > 0;
    const snapCellSet =
        matchFx?.cells?.length && !revealActive ? new Set(matchFx.cells) : null;

    const tileSnapSx = (visualCellIdx) =>
        snapCellSet?.has(visualCellIdx)
            ? {
                  animation: `${cellSnapLockKeyframes} ${LINE_COMPLETE_SNAP_MS}ms cubic-bezier(0.34, 1.45, 0.64, 1) both`,
                  willChange: "transform",
                  zIndex: 3,
              }
            : {};

    const tileSizeExpr = `calc((100% - ${(gs - 1) * SLIDING_GRID_GAP_PX}px) / ${gs})`;
    const tileStepExpr = `calc((100% - ${(gs - 1) * SLIDING_GRID_GAP_PX}px) / ${gs} + ${SLIDING_GRID_GAP_PX}px)`;

    const tilePositionSx = (visualCellIdx) => {
        const col = visualCellIdx % gs;
        const row = Math.floor(visualCellIdx / gs);
        return {
            left: `calc(${col} * (${tileStepExpr}))`,
            top: `calc(${row} * (${tileStepExpr}))`,
            width: tileSizeExpr,
            height: tileSizeExpr,
        };
    };

    const renderTileContent = (sliceIndex, isEmpty) => {
        const col = sliceIndex % gs;
        const row = Math.floor(sliceIndex / gs);
        /** Solved: fill the hole with the last slice so the photo is complete. */
        if (showTileNumbers && !isSolved && !isEmpty) {
            const darkNumbers = tileNumberTheme === "dark";
            return (
                <Box
                    aria-hidden
                    sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        containerType: "size",
                        background: darkNumbers
                            ? "linear-gradient(145deg, #2d6a4f 0%, #1b4332 52%, #081c15 100%)"
                            : "linear-gradient(145deg, #ffffff 0%, #f4f0e6 48%, #e4dcc8 100%)",
                        color: darkNumbers ? "#e8f5ee" : "#1b4332",
                        userSelect: "none",
                        pointerEvents: "none",
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            fontWeight: 800,
                            lineHeight: 1,
                            fontSize: gs >= 6 ? "46cqi" : "52cqi",
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        {sliceIndex + 1}
                    </Box>
                </Box>
            );
        }
        if (isEmpty && !isSolved) {
            if (solidEmptySlot) {
                return (
                    <Box
                        sx={(theme) => ({
                            position: "relative",
                            width: "100%",
                            height: "100%",
                            backgroundColor: slidingEmptySlotBackground(theme, 1),
                        })}
                    />
                );
            }
            return (
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        component="img"
                        src={imageUrl}
                        alt=""
                        draggable={false}
                        referrerPolicy="no-referrer"
                        sx={{
                            position: "absolute",
                            left: 0,
                            top: 0,
                            width: `${gs * 100}%`,
                            height: `${gs * 100}%`,
                            maxWidth: "none",
                            objectFit: "fill",
                            transform: `translate(-${((emptyIdx % gs) * 100) / gs}%, -${(Math.floor(emptyIdx / gs) * 100) / gs}%)`,
                            transformOrigin: "top left",
                            userSelect: "none",
                            pointerEvents: "none",
                        }}
                    />
                    <Box
                        sx={(theme) => ({
                            position: "absolute",
                            inset: 0,
                            backgroundColor: slidingEmptySlotBackground(theme),
                            pointerEvents: "none",
                        })}
                    />
                </Box>
            );
        }
        return (
            <Box
                component="img"
                src={imageUrl}
                alt=""
                draggable={false}
                referrerPolicy="no-referrer"
                sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: `${gs * 100}%`,
                    height: `${gs * 100}%`,
                    maxWidth: "none",
                    objectFit: "fill",
                    transform: `translate(-${(col * 100) / gs}%, -${(row * 100) / gs}%)`,
                    transformOrigin: "top left",
                    userSelect: "none",
                    pointerEvents: "none",
                }}
            />
        );
    };

    const renderSmoothTiles = () =>
        Array.from({ length: n }, (_, sliceIndex) => {
            const visualCellIdx = layout.findIndex((v) => Number(v) === sliceIndex);
            if (visualCellIdx < 0) return null;
            const isEmpty = sliceIndex === emptyIdx;
            const switchable =
                !readOnly &&
                !revealActive &&
                emptyVisualPos >= 0 &&
                canSlideTowardEmpty(gs, visualCellIdx, emptyVisualPos);
            const revealTransform = revealActive
                ? getTileRevealTransform(visualCellIdx, gs)
                : "translate(0, 0)";

            return (
                <Box
                    key={`slice-${sliceIndex}`}
                    onClick={switchable ? () => handleTileActivate(visualCellIdx) : undefined}
                    role={switchable ? "button" : undefined}
                    tabIndex={switchable ? 0 : undefined}
                    onKeyDown={
                        switchable
                            ? (e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      handleTileActivate(visualCellIdx);
                                  }
                              }
                            : undefined
                    }
                    sx={{
                        position: "absolute",
                        ...tilePositionSx(visualCellIdx),
                        overflow: "hidden",
                        borderRadius: "2px",
                        cursor: switchable ? "pointer" : "default",
                        outline: "none",
                        zIndex: isEmpty ? 0 : 1,
                        transform: revealTransform,
                        opacity: revealActive ? 0 : 1,
                        transition: revealActive
                            ? `transform ${SOLVE_REVEAL_MS}ms ease-in, opacity ${SOLVE_REVEAL_MS}ms ease-in`
                            : `left ${slideTransitionMs}ms ease, top ${slideTransitionMs}ms ease`,
                        ...(switchable
                            ? {
                                  "&:hover": {
                                      outline: "4px solid #7FB85A",
                                      outlineOffset: 0,
                                      zIndex: 2,
                                  },
                              }
                            : {}),
                        ...tileSnapSx(visualCellIdx),
                    }}
                >
                    {renderTileContent(sliceIndex, isEmpty)}
                </Box>
            );
        });

    const resolvedMaxBoard =
        maxBoardWidth == null
            ? "100%"
            : typeof maxBoardWidth === "number"
              ? { xs: "100%", sm: maxBoardWidth }
              : maxBoardWidth;

    return (
        <Box
            sx={{
                width: "100%",
                maxWidth: resolvedMaxBoard,
                ...(fillParent ? { height: "100%", minHeight: 0 } : {}),
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    ...(useSmoothSlides
                        ? {}
                        : {
                              display: "grid",
                              gridTemplateColumns: `repeat(${gs}, minmax(0, 1fr))`,
                              gridTemplateRows: `repeat(${gs}, minmax(0, 1fr))`,
                          }),
                    gap: revealActive || useSmoothSlides ? 0 : `${SLIDING_GRID_GAP_PX}px`,
                    width: "100%",
                    aspectRatio: "1 / 1",
                    ...(fillParent ? { height: "100%", width: "100%", minHeight: 0, minWidth: 0 } : { width: "100%" }),
                    border: revealActive ? 0 : 1,
                    borderColor: gridBorderColor ?? "divider",
                    borderRadius: 1,
                    overflow: revealActive ? "visible" : "hidden",
                    bgcolor: revealImageVisible ? "transparent" : gridGutterColor ?? "action.hover",
                    transition: `gap ${SOLVE_REVEAL_MS}ms ease, border-width ${SOLVE_REVEAL_MS}ms ease, background-color 400ms ease`,
                    pointerEvents: readOnly || revealActive ? "none" : "auto",
                }}
            >
                {revealImageVisible ? (
                    <Box
                        component="img"
                        src={imageUrl}
                        alt=""
                        draggable={false}
                        referrerPolicy="no-referrer"
                        sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                            borderRadius: 1,
                            opacity: revealActive ? 1 : 0,
                            transition: `opacity ${SOLVE_REVEAL_MS * 0.55}ms ease`,
                            zIndex: 0,
                            userSelect: "none",
                            pointerEvents: "none",
                        }}
                    />
                ) : null}
                {useSmoothSlides
                    ? renderSmoothTiles()
                    : layout.map((sliceIndexAtCell, visualCellIdx) => {
                    const sliceIndex = Number(sliceIndexAtCell);
                    const isEmpty = sliceIndex === emptyIdx;

                    const col = sliceIndex % gs;
                    const row = Math.floor(sliceIndex / gs);

                    const switchable =
                        !readOnly &&
                        !revealActive &&
                        emptyVisualPos >= 0 &&
                        canSlideTowardEmpty(gs, visualCellIdx, emptyVisualPos);

                    const revealTransform = revealActive
                        ? getTileRevealTransform(visualCellIdx, gs)
                        : "translate(0, 0)";

                    return (
                        <Box
                            key={`cell-${visualCellIdx}`}
                            onClick={
                                switchable ? () => handleTileActivate(visualCellIdx) : undefined
                            }
                            role={switchable ? "button" : undefined}
                            tabIndex={switchable ? 0 : undefined}
                            onKeyDown={
                                switchable
                                    ? (e) => {
                                          if (e.key === "Enter" || e.key === " ") {
                                              e.preventDefault();
                                              handleTileActivate(visualCellIdx);
                                          }
                                      }
                                    : undefined
                            }
                            sx={{
                                position: "relative",
                                minWidth: 0,
                                minHeight: 0,
                                width: "100%",
                                height: "100%",
                                overflow: "hidden",
                                borderRadius: revealActive ? 0 : "2px",
                                cursor: switchable ? "pointer" : "default",
                                outline: "none",
                                zIndex: 1,
                                transform: revealTransform,
                                opacity: revealActive ? 0 : 1,
                                transition: revealActive
                                    ? `transform ${SOLVE_REVEAL_MS}ms ease-in, opacity ${SOLVE_REVEAL_MS}ms ease-in`
                                    : "none",
                                /* No tile border — gutters come only from grid `gap` (matches reference line spacing). */
                                ...(switchable
                                    ? {
                                          "&:hover": {
                                              outline: "4px solid #7FB85A",
                                              outlineOffset: 0,
                                              zIndex: 2,
                                          },
                                      }
                                    : {}),
                                "&:focus-visible": switchable
                                    ? {
                                          outline: (theme) => `4px solid ${theme.palette.primary.main}`,
                                          outlineOffset: 0,
                                          zIndex: 2,
                                      }
                                    : {},
                                ...tileSnapSx(visualCellIdx),
                            }}
                        >
                            {renderTileContent(sliceIndex, isEmpty)}
                        </Box>
                    );
                })}
                {showStartClickHints && startClickHintsOn && !readOnly && !isSolved && !revealActive && !ghostGuide?.moves?.length
                    ? movableTargetsOf(gs, emptyVisualPos).map((neighbor) => (
                          <Box
                              key={`click-hint-${neighbor.cell}`}
                              aria-hidden
                              sx={{
                                  position: "absolute",
                                  ...tilePositionSx(neighbor.cell),
                                  zIndex: 8,
                                  pointerEvents: "none",
                                  display: "flex",
                                  alignItems:
                                      neighbor.point === "up"
                                          ? "flex-end"
                                          : neighbor.point === "down"
                                            ? "flex-start"
                                            : "center",
                                  justifyContent:
                                      neighbor.point === "left"
                                          ? "flex-end"
                                          : neighbor.point === "right"
                                            ? "flex-start"
                                            : "center",
                                  pt: neighbor.point === "down" ? 1 : 0,
                                  pb: neighbor.point === "up" ? 1 : 0,
                                  pl: neighbor.point === "right" ? 1 : 0,
                                  pr: neighbor.point === "left" ? 1 : 0,
                              }}
                          >
                              <Box
                                  sx={{
                                      width: 36,
                                      height: 36,
                                      borderRadius: "50%",
                                      bgcolor: "rgba(255,255,255,0.96)",
                                      border: "2px solid #7FB85A",
                                      boxShadow: "0 2px 10px rgba(15,23,42,0.4)",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      pointerEvents: "none",
                                      "@keyframes slidingClickHintPulse": {
                                          "0%, 100%": { transform: "scale(1)" },
                                          "50%": { transform: "scale(1.12)" },
                                      },
                                      animation: "slidingClickHintPulse 1.15s ease-in-out infinite",
                                  }}
                              >
                                  <Box
                                      component="svg"
                                      viewBox="0 0 24 24"
                                      sx={{
                                          width: 18,
                                          height: 18,
                                          display: "block",
                                          pointerEvents: "none",
                                          transform: `rotate(${clickHintRotation(neighbor.point)}deg)`,
                                      }}
                                  >
                                      <path
                                          d="M5 3 L5 17 L9 13.2 L12.2 19.2 L14.6 18 L11.4 12.1 L16.5 12.1 Z"
                                          fill="#1f3d2a"
                                          stroke="#ffffff"
                                          strokeWidth="0.8"
                                          strokeLinejoin="round"
                                      />
                                  </Box>
                              </Box>
                          </Box>
                      ))
                    : null}
                {matchFx && !revealActive ? (
                    <SlidingLineCompleteFx
                        fxId={matchFx.id}
                        gridSize={gs}
                        rows={matchFx.rows}
                        cols={matchFx.cols}
                        praise={matchFx.praise}
                        onComplete={clearMatchFx}
                    />
                ) : null}
                {ghostGuide?.moves?.length && !revealActive
                    ? (() => {
                          const moves =
                              ghostGuide.mode === "step"
                                  ? ghostGuide.stepIndex != null
                                      && ghostGuide.moves[ghostGuide.stepIndex]
                                      ? [ghostGuide.moves[ghostGuide.stepIndex]]
                                      : []
                                  : ghostGuide.moves;
                          return moves.map((move, idx) => {
                              const from = Number(move.from);
                              const to = Number(move.to);
                              const sliceIndex = Number(move.sliceIndex);
                              if (!Number.isFinite(from) || !Number.isFinite(to)) return null;
                              const fromCol = from % gs;
                              const fromRow = Math.floor(from / gs);
                              const toCol = to % gs;
                              const toRow = Math.floor(to / gs);
                              const dx = toCol - fromCol;
                              const dy = toRow - fromRow;
                              const stepLabel =
                                  ghostGuide.mode === "all"
                                      ? String(idx + 1)
                                      : String((ghostGuide.stepIndex ?? 0) + 1);
                              return (
                                  <React.Fragment
                                      key={`ghost-${ghostGuide.mode}-${from}-${to}-${idx}`}
                                  >
                                      <Box
                                          aria-hidden
                                          sx={{
                                              position: "absolute",
                                              ...tilePositionSx(from),
                                              zIndex: 6,
                                              pointerEvents: "none",
                                              overflow: "hidden",
                                              borderRadius: "2px",
                                              outline: "3px solid rgba(59, 166, 102, 0.95)",
                                              outlineOffset: 0,
                                              boxShadow: "0 0 12px rgba(59, 166, 102, 0.55)",
                                              bgcolor: "rgba(255,255,255,0.2)",
                                              "@keyframes slidingGhostPulse": {
                                                  "0%, 100%": { opacity: 0.55 },
                                                  "50%": { opacity: 0.95 },
                                              },
                                              "@keyframes slidingGhostSlide": {
                                                  "0%": {
                                                      transform: "translate(0, 0)",
                                                      opacity: 0.9,
                                                  },
                                                  "70%": {
                                                      opacity: 0.85,
                                                  },
                                                  "100%": {
                                                      transform: `translate(calc(${dx} * (${tileStepExpr})), calc(${dy} * (${tileStepExpr})))`,
                                                      opacity: 0.15,
                                                  },
                                              },
                                              animation:
                                                  ghostGuide.mode === "step"
                                                      ? "slidingGhostSlide 0.8s ease-in-out forwards"
                                                      : "slidingGhostPulse 1.2s ease-in-out infinite",
                                          }}
                                      >
                                          {renderTileContent(sliceIndex, false)}
                                          {ghostGuideShowLabels && ghostGuide.mode === "step" ? (
                                              <Box
                                                  sx={{
                                                      position: "absolute",
                                                      inset: 0,
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                  }}
                                              >
                                                  <Box
                                                      sx={{
                                                          minWidth: 26,
                                                          height: 26,
                                                          px: 0.5,
                                                          borderRadius: "999px",
                                                          bgcolor: "#3BA666",
                                                          color: "#fff",
                                                          fontSize: "0.8rem",
                                                          fontWeight: 700,
                                                          display: "flex",
                                                          alignItems: "center",
                                                          justifyContent: "center",
                                                          boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
                                                      }}
                                                  >
                                                      {stepLabel}
                                                  </Box>
                                              </Box>
                                          ) : null}
                                      </Box>
                                      {/* In "all" mode, keep step numbers fully opaque and centered. */}
                                      {ghostGuideShowLabels && ghostGuide.mode === "all" ? (
                                          <Box
                                              aria-hidden
                                              sx={{
                                                  position: "absolute",
                                                  ...tilePositionSx(from),
                                                  zIndex: 7,
                                                  pointerEvents: "none",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                              }}
                                          >
                                              <Box
                                                  sx={{
                                                      minWidth: 26,
                                                      height: 26,
                                                      px: 0.5,
                                                      borderRadius: "999px",
                                                      bgcolor: "#3BA666",
                                                      color: "#fff",
                                                      fontSize: "0.8rem",
                                                      fontWeight: 700,
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
                                                  }}
                                              >
                                                  {stepLabel}
                                              </Box>
                                          </Box>
                                      ) : null}
                                  </React.Fragment>
                              );
                          });
                      })()
                    : null}
            </Box>
            {isSolved && !hideCompletedCaption && !animateSolveReveal ? (
                <Typography
                    variant="subtitle2"
                    color="success.main"
                    sx={{ mt: 1, textAlign: "center", fontWeight: 600 }}
                >
                    {completedLabel ?? "Completed"}
                </Typography>
            ) : null}
        </Box>
    );
}

SlidingPlayMain.propTypes = {
    imageUrl: PropTypes.string,
    gridSize: PropTypes.number,
    startingLayout: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    ),
    emptyTileIndex: PropTypes.number,
    maxBoardWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    onLayoutChange: PropTypes.func,
    onMove: PropTypes.func,
    completedLabel: PropTypes.string,
    hideCompletedCaption: PropTypes.bool,
    animateSolveReveal: PropTypes.bool,
    onSolvedRevealComplete: PropTypes.func,
    fillParent: PropTypes.bool,
    playMoveSound: PropTypes.bool,
    ghostGuide: PropTypes.shape({
        mode: PropTypes.oneOf(["step", "all"]),
        moves: PropTypes.arrayOf(
            PropTypes.shape({
                from: PropTypes.number,
                to: PropTypes.number,
                sliceIndex: PropTypes.number,
            }),
        ),
        stepIndex: PropTypes.number,
    }),
    ghostGuideShowLabels: PropTypes.bool,
    gridGutterColor: PropTypes.string,
    gridBorderColor: PropTypes.string,
    readOnly: PropTypes.bool,
    slideTransitionMs: PropTypes.number,
    showMatchCelebrations: PropTypes.bool,
    solidEmptySlot: PropTypes.bool,
    showStartClickHints: PropTypes.bool,
    showTileNumbers: PropTypes.bool,
    tileNumberTheme: PropTypes.oneOf(["light", "dark"]),
};
